/**
 * Rate limiter for API calls to prevent overwhelming services
 */

interface RateLimitConfig {
  maxRequests: number
  windowMs: number
}

class RateLimiter {
  private requests: Map<string, number[]> = new Map()
  private config: RateLimitConfig

  constructor(config: RateLimitConfig) {
    this.config = config
  }

  /**
   * Check if a request is allowed
   */
  isAllowed(key: string): boolean {
    const now = Date.now()
    const requests = this.requests.get(key) || []
    
    // Remove old requests outside the window
    const recentRequests = requests.filter(
      (time) => now - time < this.config.windowMs
    )

    if (recentRequests.length >= this.config.maxRequests) {
      return false
    }

    recentRequests.push(now)
    this.requests.set(key, recentRequests)
    return true
  }

  /**
   * Get time until next request is allowed (in ms)
   */
  getTimeUntilNext(key: string): number {
    const now = Date.now()
    const requests = this.requests.get(key) || []
    const recentRequests = requests.filter(
      (time) => now - time < this.config.windowMs
    )

    if (recentRequests.length < this.config.maxRequests) {
      return 0
    }

    const oldestRequest = Math.min(...recentRequests)
    return this.config.windowMs - (now - oldestRequest)
  }

  /**
   * Clear rate limit for a key
   */
  clear(key: string): void {
    this.requests.delete(key)
  }

  /**
   * Clear all rate limits
   */
  clearAll(): void {
    this.requests.clear()
  }
}

// Global rate limiters for different APIs - stricter for Overpass
export const overpassRateLimiter = new RateLimiter({
  maxRequests: 1, // Max 1 request per minute (very strict)
  windowMs: 60000, // Per minute
})

export const wikipediaRateLimiter = new RateLimiter({
  maxRequests: 50, // Max 50 requests
  windowMs: 60000, // Per minute
})

export const googlePlacesRateLimiter = new RateLimiter({
  maxRequests: 10, // Max 10 requests
  windowMs: 60000, // Per minute
})
